// Сводная таблица OLAP-конструктора.
//
// Сервер отдаёт плоские строки: детальные (все поля заполнены) и подытоги —
// у подытога в _rollup перечислены свёрнутые поля. Подытоги считает сервер
// (GROUPING SETS), потому что число чеков, средний чек и фудкост из строк
// не складываются: чек с блюдами двух категорий попал бы в итог дважды.

import { formatMoney } from 'helpers/helpers'

// Поле разреза → [колонка ключа, колонка названия] в строке ответа
export const DIMENSION_FIELDS = {
	date: ['date', null],
	week: ['week', null],
	month: ['month', null],
	weekday: ['weekday', null],
	hour: ['hour', null],
	point: ['point_id', 'point_name'],
	order_type: ['order_type_id', 'order_type_name'],
	order_type_sub: ['order_type_sub_id', 'order_type_sub_name'],
	source: ['source_id', 'source_name'],
	waiter: ['waiter_id', 'waiter_name'],
	cashier: ['cashier_id', 'cashier_name'],
	product: ['product_id', 'product_name'],
	category: ['category_id', 'category_name'],
	// Папка меню — по пути «Блюда / Мангал»: одноимённые подпапки в разных разделах не сливаются в подписи
	menu: ['menu_id', 'menu_path'],
	payment_type: ['payment_type_id', 'payment_type_name'],
	payment_category: ['payment_category_id', 'payment_category_name'],
	client: ['client_id', 'client_name'],
	organization: ['organization_id', 'organization_name'],
	region: ['region_id', 'region_name'],
	district: ['district_id', 'district_name'],
	year: ['year', null],
	quarter_hour: ['quarter_hour', null],
	hour_open: ['hour_open', null],
	quarter_hour_open: ['quarter_hour_open', null],
	opened_by: ['opened_by_id', 'opened_by_name'],
	operator: ['operator_id', 'operator_name'],
	courier: ['courier_id', 'courier_name'],
	cheque: ['cheque_id', 'cheque_number'],
	hall: ['hall_id', 'hall_name'],
	table: ['table_id', 'table_name'],
	guests: ['guests_count', null],
	shift: ['shift_id', 'shift_number'],
	preorder: ['preorder', null],
	with_discount: ['with_discount', null],
	promo_code: ['promo_code', null],
}

// Разрезы, которые сортируются по значению (время, числа), остальные — по названию
const NATURAL_ORDER = ['date', 'week', 'month', 'year', 'weekday', 'hour', 'quarter_hour', 'hour_open', 'quarter_hour_open', 'guests', 'cheque', 'shift']

const MONTHS = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december']
const WEEKDAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
const ORDER_TYPES = { 1: 'hall', 2: 'takeaway', 3: 'delivery' }

const SEP = '\u0001'

function dateLabel(value) {
	if (!value) return ''
	const [y, m, d] = String(value).split('-')
	return `${d}.${m}.${y}`
}

export function dimensionKey(row, dimension) {
	const value = row[DIMENSION_FIELDS[dimension][0]]
	return value === null || value === undefined ? '' : String(value)
}

export function dimensionLabel(row, dimension, t) {
	const [idField, nameField] = DIMENSION_FIELDS[dimension]
	const value = row[idField]
	if (value === null || value === undefined) return t('olap_empty_value')

	switch (dimension) {
		case 'date':
			return dateLabel(value)
		case 'week': {
			const start = new Date(`${value}T00:00:00`)
			const end = new Date(start)
			end.setDate(end.getDate() + 6)
			const pad = n => String(n).padStart(2, '0')
			return `${pad(start.getDate())}.${pad(start.getMonth() + 1)} – ${pad(end.getDate())}.${pad(end.getMonth() + 1)}.${end.getFullYear()}`
		}
		case 'month': {
			const [y, m] = String(value).split('-')
			return `${t(MONTHS[Number(m) - 1])} ${y}`
		}
		case 'weekday':
			return t(WEEKDAYS[Number(value) - 1])
		case 'hour':
		case 'hour_open':
			return `${String(value).padStart(2, '0')}:00`
		case 'quarter_hour':
		case 'quarter_hour_open': {
			// «13:45» → «13:45–14:00»
			const [h, m] = String(value).split(':').map(Number)
			const end = h * 60 + m + 15
			const pad = n => String(n).padStart(2, '0')
			return `${value}–${pad(Math.floor(end / 60) % 24)}:${pad(end % 60)}`
		}
		case 'preorder':
		case 'with_discount':
			return Number(value) === 1 ? t('olap_yes') : t('olap_no')
		case 'cheque':
			return t('olap_cheque_no', { number: row[nameField] || value })
		case 'shift':
			return t('olap_shift_no', { number: row[nameField] || value })
		case 'order_type':
			return ORDER_TYPES[value] ? t(ORDER_TYPES[value]) : (row[nameField] || `#${value}`)
		default:
			return row[nameField] || `#${value}`
	}
}

function compareValues(dimension, a, b) {
	if (NATURAL_ORDER.includes(dimension)) {
		const na = Number(a.key)
		const nb = Number(b.key)
		if (!isNaN(na) && !isNaN(nb) && a.key !== '' && b.key !== '') return na - nb
		return a.key < b.key ? -1 : (a.key > b.key ? 1 : 0)
	}
	// Пустое значение («не указано») — в конце
	if (a.key === '' && b.key !== '') return 1
	if (b.key === '' && a.key !== '') return -1
	return a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' })
}

// Формат показателя в ячейке
export function formatMeasure(measure, value) {
	if (value === null || value === undefined) return ''
	if (['food_cost_percent', 'margin_percent', 'service_percent'].includes(measure)) return `${formatMoney(value, 1)}%`
	if (measure === 'quantity' || measure === 'return_quantity') return formatMoney(value, 2)
	return formatMoney(value, 0)
}

/**
 * Строит модель сводной таблицы.
 *
 * @returns {
 *   root,            узел общего итога, у узлов children
 *   columns,         колонки [{key, labels}] в порядке показа
 *   cell(node, colKey) → показатели ячейки ('__total' — итог строки)
 * }
 */
export function buildPivot(rows, layout, t) {
	const R = layout.rows || []
	const C = layout.columns || []

	const cells = new Map()
	const nodes = new Map()
	const columns = new Map()

	const root = { key: '', level: 0, label: t('olap_total'), labels: [], children: [] }
	nodes.set('', root)

	function ensureNode(row, level) {
		const parts = R.slice(0, level).map(dimension => dimensionKey(row, dimension))
		// Уровень в ключе обязателен: у группы «не указано» значение пустое,
		// и без уровня её ключ совпал бы с общим итогом
		const key = level === 0 ? '' : level + SEP + parts.join(SEP)
		if (nodes.has(key)) return nodes.get(key)
		const parent = ensureNode(row, level - 1)
		const dimension = R[level - 1]
		const node = {
			key,
			level,
			dimension,
			valueKey: parts[parts.length - 1],
			label: dimensionLabel(row, dimension, t),
			labels: [...parent.labels, dimensionLabel(row, dimension, t)],
			children: [],
		}
		nodes.set(key, node)
		parent.children.push(node)
		return node
	}

	for (const row of rows) {
		const rollup = row._rollup || []
		const columnsRolled = C.length === 0 || C.every(dimension => rollup.includes(dimension))
		let level = R.findIndex(dimension => rollup.includes(dimension))
		if (level === -1) level = R.length

		const node = ensureNode(row, level)

		let columnKey = '__total'
		if (!columnsRolled) {
			columnKey = C.map(dimension => dimensionKey(row, dimension)).join(SEP)
			if (!columns.has(columnKey)) {
				columns.set(columnKey, {
					key: columnKey,
					parts: C.map(dimension => ({ key: dimensionKey(row, dimension), label: dimensionLabel(row, dimension, t) })),
				})
			}
		}

		if (!cells.has(node.key)) cells.set(node.key, new Map())
		cells.get(node.key).set(columnKey, row)
	}

	const sortedColumns = [...columns.values()].sort((a, b) => {
		for (let i = 0; i < C.length; i++) {
			const result = compareValues(C[i], a.parts[i], b.parts[i])
			if (result !== 0) return result
		}
		return 0
	}).map(column => ({ key: column.key, labels: column.parts.map(part => part.label) }))

	function cell(node, columnKey) {
		const byColumn = cells.get(node.key)
		return byColumn ? (byColumn.get(columnKey) || null) : null
	}

	return { root, columns: sortedColumns, cell }
}

/**
 * Сортировка детей каждого уровня: по значению разреза или по итогу показателя.
 */
export function sortTree(node, pivot, sort) {
	node.children.sort((a, b) => {
		if (sort && sort.measure) {
			const va = Number(pivot.cell(a, '__total')?.[sort.measure] ?? 0)
			const vb = Number(pivot.cell(b, '__total')?.[sort.measure] ?? 0)
			if (va !== vb) return sort.direction === 'asc' ? va - vb : vb - va
		}
		return compareValues(a.dimension, { key: a.valueKey, label: a.label }, { key: b.valueKey, label: b.label })
	})
	node.children.forEach(child => sortTree(child, pivot, sort))
}

/**
 * Плоский список строк для показа: узлы в порядке обхода, свёрнутые — без детей.
 * Общий итог — последней строкой.
 */
export function flattenTree(root, collapsed, maxLevel) {
	const result = []
	function walk(node) {
		for (const child of node.children) {
			const hasChildren = child.level < maxLevel && child.children.length > 0
			result.push({ node: child, hasChildren })
			if (hasChildren && !collapsed.has(child.key)) walk(child)
		}
	}
	walk(root)
	return result
}
